import { ClientTable } from "../../../components/ui/ClientTable";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import i18n from "../../../i18n";import { Trans } from "react-i18next";import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Check,
  X,
  AlertTriangle,
  Clock,
  Plus,
  Star } from
'lucide-react';
import { AppLayout } from '../../../components/layout';
import { PageBackLink } from '../../../components/layout/PageBackLink';
import { LoadingState, EmptyState, ui } from '../../../components/ui';
import { cn } from '../../../lib/utils';
import { hasCanonicalRole, hasAnyCanonicalRole } from '../../../lib/auth';
import { Button } from '../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';

import {
  fetchComplaintDetail,
  fetchCustomerAssetById,
  fetchCustomerLocationById,
  resolveAmcTypeLabel,
  amcTypesMatch,
  formatCallTypeLabel,
  updateManagerComplaintStatus,
  mapManagerActionToStatus,
  MANAGER_STATUS_ACTIONS,
  searchTechnicians,
  buildUpdateComplaintPayload,
  updateCustomerComplaint,
  scheduleComplaintVisit,
  hasTechnicianAccepted,
  fetchChecklistItemsSplitByWho,
  getComplaintChecklistSubmissions,
  findLatestChecklistSubmission,
  getChecklistSubmissionStatus,
  mapQuotations } from
'../../../services/fsmService';
import { getQuotationsForComplaintId } from '../../../services/quotationService';
import sessionDB from '../../../utils/sessionDB';
import { TICKET_STATUS_CLASS, translateTicketStatus, translateTicketPriority, getPriorityClass } from '../data/ticketConstants';
import { FsmQuotationCard } from '../components/FsmQuotationCard';
import { FsmCollapsibleCard } from '../components/FsmCollapsibleCard';

const CHECKLIST_STATUS_OPTIONS = [
'Pending',
'In Progress',
'Completed',
'Not Applicable'];

const CLOSURE_MODE_OPTIONS = [
{ value: 'Email', labelKey: 'common.email' },
{ value: 'Phone', labelKey: 'common.phone' },
{ value: 'Whatsapp', labelKey: 'fsm.whatsapp' }];

const RATING_SCALE = [1, 2, 3, 4, 5];

function RatingPicker({ value, onChange, disabled = false }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {RATING_SCALE.map((n) => {
        const active = Number(value) === n;
        return (
          <button
            key={n}
            type="button"
            disabled={disabled}
            onClick={() => onChange?.(n)}
            className={cn(
              'h-11 w-11 rounded-xl border text-sm font-bold transition inline-flex flex-col items-center justify-center gap-0.5',
              active ?
              'border-accent bg-accent/5 text-accent' :
              'border-(--color-text-border) bg-white text-foreground hover:bg-workspace/50',
              disabled && 'opacity-60 cursor-default'
            )}>
            
            <Star className={cn('h-3.5 w-3.5', active && 'fill-current')} />
            {n}
          </button>);

      })}
    </div>);

}

function ReadOnlyField({ label, children }) {
  return (
    <div className="space-y-1">
      <span className="block text-[10px] font-bold uppercase tracking-wider per-file-hint">
        {label}
      </span>
      <div className="text-sm text-foreground/90 whitespace-pre-wrap wrap-break-word">
        {children}
      </div>
    </div>);

}

function normalizeChecklistStatus(value) {
  const raw = String(value || '').trim();
  if (!raw) return 'Pending';
  const match = CHECKLIST_STATUS_OPTIONS.find(
    (opt) => opt.toLowerCase() === raw.toLowerCase()
  );
  return match || 'Pending';
}

function formatDisplayDate(iso) {
  if (!iso) return '—';
  const datePart = String(iso).slice(0, 10);
  const [y, m, d] = datePart.split('-');
  if (!y || !m || !d) return '—';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d} ${months[parseInt(m, 10) - 1]} ${y}`;
}

/** Parse API visitDate into datetime-local value (YYYY-MM-DDTHH:mm). */
function toDateTimeLocalValue(value) {
  if (!value) return '';
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return `${raw}T00:00`;
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})/);
  if (match) return `${match[1]}T${match[2]}:${match[3]}`;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) return raw.slice(0, 16);
  return '';
}

/** Display visit date + time, e.g. "31 Jul 2026, 03:03". */
function formatVisitDateTime(value) {
  const local = toDateTimeLocalValue(value);
  if (!local) return '—';
  const [datePart, timePart = ''] = local.split('T');
  const [y, m, d] = datePart.split('-');
  if (!y || !m || !d) return '—';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dateLabel = `${d} ${months[parseInt(m, 10) - 1]} ${y}`;
  return timePart ? `${dateLabel}, ${timePart}` : dateLabel;
}

function isPendingReviewStatus(status) {
  const key = String(status || '')
  .trim()
  .toLowerCase()
  .replace(/[\s-]+/g, '_');
  return key === 'pending_review' || key === 'under_review';
}

function isOnHoldStatus(status) {
  const key = String(status || '')
  .trim()
  .toLowerCase()
  .replace(/[\s-]+/g, '_');
  return key === 'on_hold' || key === 'hold';
}

function isRejectedStatus(status) {
  const key = String(status || '')
  .trim()
  .toLowerCase()
  .replace(/[\s-]+/g, '_');
  return key === 'rejected' || key === 'reject';
}

function formatWarrantyLabel(warrantyType) {
  if (!warrantyType) return '—';
  return String(warrantyType).replace(/_/g, ' ');
}

function buildInstallationNote(asset, amcLabel) {
  if (!asset) return '';
  const parts = [];
  if (asset.warrantyExpiry) {
    parts.push(`Warranty valid till ${formatDisplayDate(asset.warrantyExpiry)}.`);
  }
  if (amcLabel) {
    const until = asset.amcEndDate ?
    ` until ${formatDisplayDate(asset.amcEndDate)}` :
    '';
    parts.push(`${amcLabel} active${until}.`);
  }
  return parts.join(' ');
}

const EMPTY_COMPANY_RECORD = {
  customerPriority: '—',
  system: '—',
  complaintType: '—',
  product: '—',
  warranty: '—',
  amc: '—',
  installationCode: '—',
  installationDate: '—',
  installationNote: '',
  mismatch: false
};

export function FsmManagerReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // --- Load complaint details from API ---
  // Defer fetch so React Strict Mode's immediate remount cancels the first
  // scheduled call — avoids duplicate detail API requests on navigate/mount.
  useEffect(() => {
    if (!id) return;

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      if (controller.signal.aborted) return;

      (async () => {
        setLoading(true);
        setLoadError('');
        try {
          const detail = await fetchComplaintDetail(id);
          if (controller.signal.aborted) return;
          if (!detail) {
            setTicket(null);
            setLoadError('Complaint not found.');
          } else {
            setTicket(detail);
          }
        } catch (err) {
          console.error('Failed to load complaint details:', err);
          if (!controller.signal.aborted) {
            setTicket(null);
            setLoadError(
              err.status === 401 ?
              'Session expired. Please refresh and try again.' :
              err.message || 'Failed to load complaint details.'
            );
          }
        } finally {
          if (!controller.signal.aborted) setLoading(false);
        }
      })();
    }, 0);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [id]);

  // --- Section 1: Claim vs Company Records (from customer-assets API) ---
  const [companyRecord, setCompanyRecord] = useState(null);
  const [companyRecordLoading, setCompanyRecordLoading] = useState(false);

  useEffect(() => {
    if (!ticket?.assetId) {
      setCompanyRecord(null);
      setCompanyRecordLoading(false);
      return;
    }

    const assetId = ticket.assetId;
    const priority = ticket.priority;
    const complaintType = ticket.complaintType;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      if (controller.signal.aborted) return;

      (async () => {
        setCompanyRecordLoading(true);
        try {
          const asset = await fetchCustomerAssetById(assetId);
          if (controller.signal.aborted) return;
          if (!asset) {
            setCompanyRecord(null);
            return;
          }

          const amcLabel = resolveAmcTypeLabel(asset) || '';

          setCompanyRecord({
            customerPriority: priority || '—',
            system: asset.assetCategory || '—',
            complaintType: complaintType || '—',
            product: asset.assetName || '—',
            warranty: formatWarrantyLabel(asset.warrantyType),
            amc: amcLabel || '—',
            installationCode:
            asset.serialNumber || (
            asset.assetId != null ? String(asset.assetId) : '—'),
            installationDate: formatDisplayDate(asset.installDate),
            installationNote: buildInstallationNote(asset, amcLabel),
            mismatch: false
          });
        } catch (err) {
          console.error('Failed to load company asset records:', err);
          if (!controller.signal.aborted) setCompanyRecord(null);
        } finally {
          if (!controller.signal.aborted) setCompanyRecordLoading(false);
        }
      })();
    }, 0);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [ticket?.assetId, ticket?.priority, ticket?.complaintType]);

  const records = companyRecord || EMPTY_COMPANY_RECORD;

  // --- Location details for Complaint Details ---
  const [locationDetail, setLocationDetail] = useState(null);

  useEffect(() => {
    if (!ticket?.locationId) {
      setLocationDetail(null);
      return;
    }

    const locationId = ticket.locationId;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      if (controller.signal.aborted) return;

      (async () => {
        try {
          const detail = await fetchCustomerLocationById(locationId);
          if (controller.signal.aborted) return;
          setLocationDetail(detail);
        } catch (err) {
          console.error('Failed to load location details:', err);
          if (!controller.signal.aborted) setLocationDetail(null);
        }
      })();
    }, 0);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [ticket?.locationId]);

  // --- Section 2: Review States ---
  const [isProceeded, setIsProceeded] = useState(false);
  const [isHoldOpen, setIsHoldOpen] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [statusActionLoading, setStatusActionLoading] = useState('');

  // Complaint Details — from API ticket
  const address =
  locationDetail?.addressDisplay ||
  ticket?.locationName ||
  ticket?.customerSub ||
  '—';
  const siteType = locationDetail?.siteType || '—';
  const system = ticket?.system || ticket?.productServiceName || '—';
  //const priority = ticket?.priority || '—';
  const poc = ticket?.pointOfContact || '—';
  const contact = ticket?.contactNumber || '—';
  const email = ticket?.email || '—';
  const complaintMode = ticket?.complaintMode || '—';
  const complaintDate = ticket?.complaintRecievedOn || ticket?.complaintDate ?
  formatVisitDateTime(ticket.complaintRecievedOn || ticket.complaintDate) :
  '—';
  const complaintType = ticket?.complaintType || ticket?.complaintTypeName || '—';
  const description = ticket?.description || '—';
  const product = ticket?.assetName || '—';
  const warrantyPeriod = ticket?.warrantyType ?
  String(ticket.warrantyType).replace(/_/g, ' ') :
  '—';
  const amc = resolveAmcTypeLabel(ticket) || '—';
  const callType = formatCallTypeLabel(ticket?.callType || ticket?.raw?.callType) || '—';

  // --- Section 4: Technician States ---
  const [technicians, setTechnicians] = useState([]);
  const [techniciansLoading, setTechniciansLoading] = useState(false);
  const [techniciansError, setTechniciansError] = useState('');
  const [selectedTechId, setSelectedTechId] = useState('');
  const [isReassigning, setIsReassigning] = useState(false);
  const [autoAssign, setAutoAssign] = useState(false);
  const [, setTechnicianAccepted] = useState(false);
  const [techContactReadOnly, setTechContactReadOnly] = useState('');
  // --- Section 5: Checklists from API ---
  const [internalChecklistItems, setInternalChecklistItems] = useState([]);
  const [fieldChecklistItems, setFieldChecklistItems] = useState([]);
  const [checklistsLoading, setChecklistsLoading] = useState(false);
  const [checklistsError, setChecklistsError] = useState('');

  // --- Quotations (CS / Service Manager can raise while In_Progress) ---
  const [quotations, setQuotations] = useState([]);
  const [quotationsLoading, setQuotationsLoading] = useState(false);
  const [quotationsError, setQuotationsError] = useState('');

  // --- Section 7: Scheduling State ---
  const [firstVisitDate, setFirstVisitDate] = useState('');
  const [scheduledVisits, setScheduledVisits] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);

  // --- Schedule another visit popup ---
  const [isAnotherVisitOpen, setIsAnotherVisitOpen] = useState(false);
  const [anotherVisitDate, setAnotherVisitDate] = useState('');

  // --- Closure verification / manager close ---
  const [closureMode, setClosureMode] = useState('');
  const [customerServiceRemarks, setCustomerServiceRemarks] = useState('');
  const [customerRatings, setCustomerRatings] = useState(null);
  const [managerRemarks, setManagerRemarks] = useState('');
  const [managerRatings, setManagerRatings] = useState(null);

  // --- UI Toast/Notification state ---
  const [toast, setToast] = useState(null);

  // --- Mobile Claim Details Modal States ---
  const [selectedComparisonRow, setSelectedComparisonRow] = useState(null);
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);

  useEffect(() => {
    if (!ticket) return;
    const pending =
    isPendingReviewStatus(ticket.status) || isPendingReviewStatus(ticket.statusRaw);
    const onHold =
    isOnHoldStatus(ticket.status) || isOnHoldStatus(ticket.statusRaw);
    // Under review / On Hold start with decision actions; other statuses continue the workflow
    setIsProceeded(!pending && !onHold);
    setTechnicianAccepted(hasTechnicianAccepted(ticket));
    setIsReassigning(false);
    setAutoAssign(false);

    setClosureMode(ticket.closureMode || ticket.raw?.closureMode || '');
    setCustomerServiceRemarks(
      ticket.customerClosureRemarks ||
        ticket.customerServiceRemarks ||
        ticket.raw?.customerClosureRemarks ||
        ticket.raw?.customerServiceRemarks ||
        ''
    );
    setCustomerRatings(
      ticket.customerRating != null && ticket.customerRating !== '' ?
      Number(ticket.customerRating) :
      ticket.customerRanking != null && ticket.customerRanking !== '' ?
      Number(ticket.customerRanking) :
      ticket.customerRatings != null && ticket.customerRatings !== '' ?
      Number(ticket.customerRatings) :
      ticket.raw?.customerRating != null && ticket.raw?.customerRating !== '' ?
      Number(ticket.raw.customerRating) :
      ticket.raw?.customerRanking != null && ticket.raw?.customerRanking !== '' ?
      Number(ticket.raw.customerRanking) :
      ticket.raw?.customerRatings != null && ticket.raw?.customerRatings !== '' ?
      Number(ticket.raw.customerRatings) :
      null
    );
    setManagerRemarks(
      ticket.managerClosureRemarks ||
        ticket.managerRemarks ||
        ticket.raw?.managerClosureRemarks ||
        ticket.raw?.managerRemarks ||
        ''
    );
    setManagerRatings(
      ticket.managerRating != null && ticket.managerRating !== '' ?
      Number(ticket.managerRating) :
      ticket.managerRanking != null && ticket.managerRanking !== '' ?
      Number(ticket.managerRanking) :
      ticket.managerRatings != null && ticket.managerRatings !== '' ?
      Number(ticket.managerRatings) :
      ticket.raw?.managerRating != null && ticket.raw?.managerRating !== '' ?
      Number(ticket.raw.managerRating) :
      ticket.raw?.managerRanking != null && ticket.raw?.managerRanking !== '' ?
      Number(ticket.raw.managerRanking) :
      ticket.raw?.managerRatings != null && ticket.raw?.managerRatings !== '' ?
      Number(ticket.raw.managerRatings) :
      null
    );

    const visits =
    Array.isArray(ticket.scheduledVisits) && ticket.scheduledVisits.length > 0 ?
    ticket.scheduledVisits :
    Array.isArray(ticket.raw?.scheduledVisits) ?
    ticket.raw.scheduledVisits :
    [];
    const sortedVisits = [...visits].sort((a, b) => {
      const ta = Date.parse(String(a?.visitDate || a?.createdOn || '').replace(' ', 'T')) || 0;
      const tb = Date.parse(String(b?.visitDate || b?.createdOn || '').replace(' ', 'T')) || 0;
      return ta - tb;
    });
    setScheduledVisits(sortedVisits);

    // Only hydrate from actual scheduledVisits — do not use complaint scheduledDate
    // (create flow stores complaint date there, which looks like "today" pre-filled).
    const scheduled = sortedVisits[0]?.visitDate || '';
    setFirstVisitDate(toDateTimeLocalValue(scheduled));
  }, [ticket]);

  // --- Find Selected Customer Details ---
  // --- Selected Technician Detail ---
  const selectedTechnician = useMemo(() => {
    return technicians.find((t) => t.id === selectedTechId) || null;
  }, [technicians, selectedTechId]);

  // --- Dynamic update technician contact ---
  useEffect(() => {
    if (selectedTechnician) {
      setTechContactReadOnly(selectedTechnician.contact || '');
    } else {
      setTechContactReadOnly('');
    }
  }, [selectedTechnician]);

  const isPendingReview =
  Boolean(ticket) && (
  isPendingReviewStatus(ticket.status) || isPendingReviewStatus(ticket.statusRaw));

  const ticketStatusKey = String(ticket?.statusRaw || ticket?.status || '')
  .trim()
  .toLowerCase()
  .replace(/[\s-]+/g, '_');

  const techRejectionReason = String(
    ticket?.technicianRemarks ||
      ticket?.techRemarks ||
      ticket?.remarks ||
      ticket?.raw?.technicianRemarks ||
      ticket?.raw?.techRemarks ||
      ticket?.raw?.remarks ||
      ''
  ).trim();
  const showTechRejectionBanner =
  isPendingReview && Boolean(techRejectionReason);

  const rejectedById =
  ticket?.technicianId ??
  ticket?.raw?.technicianId ??
  ticket?.updatedBy ??
  ticket?.raw?.updatedBy ??
  null;

  const rejectedByName = useMemo(() => {
    if (ticket?.technicianName) return ticket.technicianName;
    if (rejectedById == null || rejectedById === '') return '';
    const match = technicians.find((t) => String(t.id) === String(rejectedById));
    if (match?.name) return match.name;
    return `Employee #${rejectedById}`;
  }, [ticket?.technicianName, rejectedById, technicians]);

  const isOnHold =
  isOnHoldStatus(ticket?.status) || isOnHoldStatus(ticket?.statusRaw);
  const isRejected =
  isRejectedStatus(ticket?.status) || isRejectedStatus(ticket?.statusRaw);
  const isOnHoldOrRejected = isOnHold || isRejected;

  const isActiveWorkflowStatus = [
  'assigned',
  'accepted',
  'scheduled',
  'in_progress',
  'quotation_submitted',
  'quotation_accepted',
  'closure_requested',
  'closure_verified',
  'closed']
  .includes(ticketStatusKey);

  // After tech accepts, status is Accepted; manager then schedules
  const managerDisplayStatus = ticket?.status;
  const isAcceptedStatus = ticketStatusKey === 'accepted';
  const isInProgressStatus = ticketStatusKey === 'in_progress';
  const isQuotationSubmitted = ticketStatusKey === 'quotation_submitted';
  const isQuotationAccepted = ticketStatusKey === 'quotation_accepted';
  const isQuotationPhase = isQuotationSubmitted || isQuotationAccepted;
  const isScheduledOrLater = [
  'scheduled',
  'in_progress',
  'quotation_submitted',
  'quotation_accepted',
  'closure_requested',
  'closure_verified',
  'closed']
  .includes(ticketStatusKey);
  const isClosureRequested = ticketStatusKey === 'closure_requested';
  const isClosureVerified = ticketStatusKey === 'closure_verified';
  const isClosedStatus = ticketStatusKey === 'closed';
  const isCustomerService = hasCanonicalRole('customer_service');
  // Service_Manager / Manager can close after CS verification (even if they also have Customer_Service)
  const isServiceManager = hasAnyCanonicalRole(['service_manager', 'manager']);
  const canVerifyClosure = isCustomerService && isClosureRequested;
  const canManagerClose = isServiceManager && isClosureVerified;
  const showClosurePanel =
  isClosureRequested || isClosureVerified || isClosedStatus;
  const savedTechnicianId = ticket?.technicianId ?? ticket?.raw?.technicianId ?? '';
  const hasSavedTechnician = savedTechnicianId != null && String(savedTechnicianId) !== '';
  const assignmentEditable =
    !isClosureRequested &&
    !isClosureVerified &&
    !isClosedStatus &&
    (!hasSavedTechnician || isReassigning);
  const showManagerReviewActions = isPendingReview && !isProceeded && !isOnHoldOrRejected;
  // On Hold tickets can be Rejected or Proceeded back into assignment
  const showOnHoldActions = isOnHold && !isProceeded;
  // Technician card after Proceed (including resume from On Hold), or active workflow — never for Rejected
  const showTechnicianCard =
  Boolean(ticket) &&
  !isRejected && (
  isProceeded || isActiveWorkflowStatus);
  const canReassignTechnician =
    showTechnicianCard &&
    hasSavedTechnician &&
    !isClosureRequested &&
    !isClosureVerified &&
    !isClosedStatus;
  // Scheduling after tech accepts, or when Auto Assign is checked during assignment
  const isAutoAssignScheduling = autoAssign && assignmentEditable;
  const showSchedulingSection =
  showTechnicianCard && (isAcceptedStatus || isScheduledOrLater || isAutoAssignScheduling);
  // Checklists after a visit has been scheduled
  const showAssignmentDetails = showTechnicianCard && isScheduledOrLater;
  const canSaveAssignment = showTechnicianCard && assignmentEditable;
  const canSaveSchedule =
  showSchedulingSection &&
  !isClosureRequested &&
  !isClosureVerified &&
  !isClosedStatus &&
  !isReassigning;

  const canRaiseQuotation =
  (isCustomerService || isServiceManager) &&
  (isInProgressStatus || isQuotationSubmitted) &&
  !isClosureRequested &&
  !isClosureVerified &&
  !isClosedStatus;
  const showQuotationSection =
  showAssignmentDetails &&
  (isInProgressStatus || isQuotationPhase || isClosureRequested || isClosureVerified || isClosedStatus);
  const complaintIdForQuotations =
    ticket?.complaintId ??
    ticket?.id ??
    ticket?.raw?.complaintId ??
    id;
  const hasQuotations = quotations.length > 0;
  // Auto Assign: always allow picking/changing first visit date-time.
  // Normal schedule flow: editable only while Accepted and no visits listed yet.
  const canEditFirstVisit = isAutoAssignScheduling ?
  true :
  isAcceptedStatus && scheduledVisits.length === 0;
  const resolutionText = String(
    ticket?.technicianRemarks ||
      ticket?.techRemarks ||
      ticket?.remarks ||
      ticket?.raw?.technicianRemarks ||
      ticket?.raw?.techRemarks ||
      ticket?.raw?.remarks ||
      ''
  ).trim();
  const selectedClosureMode = CLOSURE_MODE_OPTIONS.find((opt) => opt.value === closureMode);
  const closureModeLabel = selectedClosureMode ?
  i18n.t(selectedClosureMode.labelKey) :
  closureMode || '—';

  const checklistSubmissions = getComplaintChecklistSubmissions(ticket);

  const complaintTypeIdForChecklist =
  ticket?.complaintTypeId ??
  ticket?.raw?.complaintTypeId ??
  ticket?.raw?.complaint?.complaintTypeId ??
  null;

  // Load Internal Asset + Field Service checklists as soon as a technician is selected.
  useEffect(() => {
    if (!showAssignmentDetails || complaintTypeIdForChecklist == null || complaintTypeIdForChecklist === '') {
      setInternalChecklistItems([]);
      setFieldChecklistItems([]);
      setChecklistsError(
        showAssignmentDetails && (complaintTypeIdForChecklist == null || complaintTypeIdForChecklist === '') ?
        'Complaint type is missing on this ticket; cannot load checklists.' :
        ''
      );
      return;
    }

    let cancelled = false;
    setChecklistsLoading(true);
    setChecklistsError('');

    fetchChecklistItemsSplitByWho(complaintTypeIdForChecklist)
    .then(({ internal, field }) => {
      if (cancelled) return;
      setInternalChecklistItems(internal);
      setFieldChecklistItems(field);
      if (internal.length === 0 && field.length === 0) {
        setChecklistsError('');
      }
    })
    .catch((err) => {
      console.error('Failed to load checklists:', err);
      if (!cancelled) {
        setInternalChecklistItems([]);
        setFieldChecklistItems([]);
        setChecklistsError(
          err.status === 401 ?
          'Session expired. Please refresh and try again.' :
          err.message || 'Failed to load checklists.'
        );
      }
    })
    .finally(() => {
      if (!cancelled) setChecklistsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [showAssignmentDetails, complaintTypeIdForChecklist]);

  useEffect(() => {
    if (!showQuotationSection || complaintIdForQuotations == null || complaintIdForQuotations === '') {
      return undefined;
    }

    const nested = mapQuotations(
      Array.isArray(ticket?.quotations) ?
      ticket.quotations :
      Array.isArray(ticket?.raw?.quotations) ?
      ticket.raw.quotations :
      Array.isArray(ticket?.raw?.quotationList) ?
      ticket.raw.quotationList :
      []
    );

    let cancelled = false;
    setQuotationsLoading(true);
    setQuotationsError('');
    if (nested.length > 0) setQuotations(nested);

    getQuotationsForComplaintId(complaintIdForQuotations)
      .then((list) => {
        if (cancelled) return;
        const mapped = mapQuotations(list);
        setQuotations(mapped.length > 0 ? mapped : nested);
      })
      .catch((err) => {
        if (cancelled) return;
        setQuotations(nested);
        if (nested.length === 0) {
          setQuotationsError(err.message || 'Failed to load quotations.');
        }
      })
      .finally(() => {
        if (!cancelled) setQuotationsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [showQuotationSection, complaintIdForQuotations, ticket?.quotations, ticket?.raw?.quotations, ticket?.raw?.quotationList]);

  const handleRaiseQuotation = () => {
    const complaintId = complaintIdForQuotations;
    if (complaintId == null || complaintId === '') return;

    navigate(
      `/sales/quotations/quotation/create?complaintId=${encodeURIComponent(complaintId)}`,
      {
        state: {
          complaintId,
          returnTo: `/fsm/customer-complaints/${ticket?.id ?? id}`,
          prefill: {
            customerId: ticket?.customerId ?? ticket?.raw?.customerId ?? null,
            customerName:
              ticket?.customerName && ticket.customerName !== '—' ?
              ticket.customerName :
              '',
            locationId: ticket?.locationId ?? ticket?.raw?.locationId ?? null,
            locationName:
              ticket?.locationName ||
              locationDetail?.locationName ||
              locationDetail?.addressDisplay ||
              '',
            contactName: ticket?.pointOfContact || '',
            phone: ticket?.contactNumber || '',
            email: ticket?.email || ''
          }
        }
      }
    );
  };

  // Load technicians when the assignment card is shown, or to resolve tech rejection "rejected by"
  useEffect(() => {
    if (!showTechnicianCard && !showTechRejectionBanner) return;

    let cancelled = false;
    setTechniciansLoading(true);
    setTechniciansError('');

    searchTechnicians({ page: 0, size: 100, sortBy: 'first_name', sortDir: 'ASC' })
    .then((list) => {
      if (cancelled) return;
      setTechnicians(list);
      const existingId = ticket?.raw?.technicianId ?? ticket?.technicianId;
      if (existingId != null && existingId !== '') {
        const match = list.find((t) => String(t.id) === String(existingId));
        if (match) setSelectedTechId(match.id);
      }
    })
    .catch((err) => {
      console.error('Failed to load technicians:', err);
      if (!cancelled) {
        setTechnicians([]);
        setTechniciansError(
          err.status === 401 ?
          'Session expired. Please refresh and try again.' :
          err.message || 'Failed to load technicians.'
        );
      }
    })
    .finally(() => {
      if (!cancelled) setTechniciansLoading(false);
    });

    return () => {cancelled = true;};
  }, [showTechnicianCard, showTechRejectionBanner, ticket?.id, ticket?.raw?.technicianId, ticket?.technicianId]);

  // --- Show Toast (auto-dismiss ~3s) ---
  const showToastMsg = (msg, type = 'success') => {
    setToast({ message: msg, type });
  };

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  // Hold / Reject → On_Hold / Rejected via manager-status API. Proceed only reveals technician card.
  const handleManagerStatusAction = async (newStatus) => {
    if (!ticket?.id || statusActionLoading) return;

    setStatusActionLoading(newStatus);
    try {
      await updateManagerComplaintStatus(ticket.id, newStatus);
      setIsHoldOpen(false);
      setIsRejectOpen(false);

      if (newStatus === MANAGER_STATUS_ACTIONS.hold) {
        navigate('/fsm/customer-complaints', { replace: true });
        return;
      }

      const nextStatus = mapManagerActionToStatus(newStatus);
      setTicket((prev) => ({
        ...prev,
        status: nextStatus,
        statusRaw: newStatus,
        raw: {
          ...(prev.raw || {}),
          complaintStatus: newStatus
        }
      }));
      setIsProceeded(false);

      const messages = {
        [MANAGER_STATUS_ACTIONS.reject]: 'Ticket rejected.'
      };
      showToastMsg(
        messages[newStatus] || 'Ticket status updated.',
        newStatus === MANAGER_STATUS_ACTIONS.reject ? 'error' : 'warning'
      );
    } catch (err) {
      console.error('Failed to update manager status:', err);
      showToastMsg(
        err.status === 401 ?
        'Session expired. Please refresh and try again.' :
        err.message || 'Failed to update ticket status.',
        'error'
      );
    } finally {
      setStatusActionLoading('');
    }
  };

  const handleProceed = () => {
    setIsProceeded(true);
    showToastMsg(
      isOnHold ?
      'Ticket resumed. Select a technician, then save changes.' :
      'Select a technician, then save changes.'
    );
  };

  const handleHoldConfirm = () => handleManagerStatusAction(MANAGER_STATUS_ACTIONS.hold);
  const handleRejectConfirm = () => handleManagerStatusAction(MANAGER_STATUS_ACTIONS.reject);

  const handleFirstVisitChange = (val) => {
    setFirstVisitDate(val);
  };

  const openAnotherVisitPopup = () => {
    setAnotherVisitDate('');
    setIsAnotherVisitOpen(true);
  };

  /** Local-only: add visit to UI list. New visits (visitId null) are created via schedule API on Save. */
  const handleAddConsecutiveVisit = () => {
    if (!anotherVisitDate) {
      showToastMsg('Please select the visit date and time.', 'error');
      return;
    }

    const managerId =
    ticket?.managerId ?? ticket?.raw?.managerId ?? sessionDB.getEmpId();
    const complaintId = ticket?.complaintId ?? ticket?.id;
    const displayVisit =
    anotherVisitDate.length >= 16 ? anotherVisitDate.slice(0, 16) : anotherVisitDate;

    setScheduledVisits((prev) => {
      let base = Array.isArray(prev) ? [...prev] : [];
      // Seed first visit from the main date field when the list is still empty
      if (base.length === 0 && firstVisitDate) {
        base.push({
          visitId: null,
          complaintId,
          visitDate: firstVisitDate,
          scheduledBy: managerId,
          isLocal: true
        });
      }
      return [
      ...base,
      {
        visitId: null,
        complaintId,
        visitDate: displayVisit,
        scheduledBy: managerId,
        isLocal: true
      }];

    });

    setIsAnotherVisitOpen(false);
    setAnotherVisitDate('');
  };

  const handleVerifyClosure = async () => {
    if (!ticket?.id || statusActionLoading) return;
    if (!closureMode) {
      showToastMsg(i18n.t('fsm.selectClosureMode'), 'error');
      return;
    }
    if (!String(customerServiceRemarks || '').trim()) {
      showToastMsg('Please enter customer service remarks.', 'error');
      return;
    }
    if (customerRatings == null || !Number.isFinite(Number(customerRatings))) {
      showToastMsg('Please select a customer rating.', 'error');
      return;
    }

    setStatusActionLoading('Closure_Verified');
    try {
      const payload = buildUpdateComplaintPayload(ticket, {
        technicianId: ticket.technicianId ?? ticket.raw?.technicianId ?? selectedTechId,
        closureMode,
        customerClosureRemarks: customerServiceRemarks.trim(),
        customerRating: Number(customerRatings),
        complaintStatus: 'Closure_Verified'
      });
      await updateCustomerComplaint(ticket.id, payload);
      showToastMsg('Closure verified successfully.');
      navigate('/fsm/customer-complaints', { replace: true });
    } catch (err) {
      console.error('Failed to verify closure:', err);
      showToastMsg(
        err.status === 401 ?
        'Session expired. Please refresh and try again.' :
        err.message || 'Failed to verify closure.',
        'error'
      );
    } finally {
      setStatusActionLoading('');
    }
  };

  const handleCloseTicket = async () => {
    if (!ticket?.id || statusActionLoading) return;
    if (!String(managerRemarks || '').trim()) {
      showToastMsg('Please enter manager remarks.', 'error');
      return;
    }
    if (managerRatings == null || !Number.isFinite(Number(managerRatings))) {
      showToastMsg('Please select a manager rating.', 'error');
      return;
    }

    setStatusActionLoading('Closed');
    try {
      const payload = buildUpdateComplaintPayload(ticket, {
        technicianId: ticket.technicianId ?? ticket.raw?.technicianId ?? selectedTechId,
        closureMode: ticket.closureMode ?? ticket.raw?.closureMode ?? closureMode,
        customerClosureRemarks:
          ticket.customerClosureRemarks ??
          ticket.raw?.customerClosureRemarks ??
          ticket.customerServiceRemarks ??
          ticket.raw?.customerServiceRemarks ??
          customerServiceRemarks,
        customerRating:
          ticket.customerRating ??
          ticket.raw?.customerRating ??
          ticket.customerRanking ??
          ticket.raw?.customerRanking ??
          ticket.customerRatings ??
          ticket.raw?.customerRatings ??
          customerRatings,
        managerClosureRemarks: managerRemarks.trim(),
        managerRating: Number(managerRatings),
        complaintStatus: 'Closed'
      });
      await updateCustomerComplaint(ticket.id, payload);
      showToastMsg('Ticket successfully marked as Closed.');
      navigate('/fsm/customer-complaints', { replace: true });
    } catch (err) {
      console.error('Failed to close ticket:', err);
      showToastMsg(
        err.status === 401 ?
        'Session expired. Please refresh and try again.' :
        err.message || 'Failed to close ticket.',
        'error'
      );
    } finally {
      setStatusActionLoading('');
    }
  };

  // --- Save Changes: assign technician only (status Assigned), then return to list ---
  const handleSaveAssignment = async () => {
    if (!ticket?.id || saveLoading) return;
    if (!selectedTechId) {
      showToastMsg('Please select a technician.', 'error');
      return;
    }

    setSaveLoading(true);
    try {
      const payload = buildUpdateComplaintPayload(ticket, {
        technicianId: selectedTechId,
        complaintStatus: 'Assigned'
      });
      await updateCustomerComplaint(ticket.id, payload);

      showToastMsg(
        isReassigning ? 'Technician reassigned successfully.' : 'Technician assigned successfully.'
      );
      setIsReassigning(false);
      setAutoAssign(false);
      navigate('/fsm/customer-complaints', { replace: true });
    } catch (err) {
      console.error('Failed to assign technician:', err);
      showToastMsg(
        err.status === 401 ?
        'Session expired. Please refresh and try again.' :
        err.message || 'Failed to assign technician.',
        'error'
      );
    } finally {
      setSaveLoading(false);
    }
  };

  /**
   * Auto Assign: assign technician + create schedule in one Save.
   * Reuses the same update + scheduleComplaintVisit sequence as handleSaveSchedule.
   * Status becomes Scheduled only after the update succeeds; redirect only if all calls succeed.
   */
  const handleSaveAutoAssign = async () => {
    if (!ticket?.id || saveLoading) return;
    if (!selectedTechId) {
      showToastMsg('Please select a technician.', 'error');
      return;
    }
    if (!firstVisitDate && scheduledVisits.length === 0) {
      showToastMsg('Please select the visit date and time.', 'error');
      return;
    }

    setSaveLoading(true);
    try {
      const managerId =
      ticket?.managerId ?? ticket?.raw?.managerId ?? sessionDB.getEmpId();
      const complaintId = ticket?.complaintId ?? ticket?.id;

      let visitsForSchedule = Array.isArray(scheduledVisits) ? [...scheduledVisits] : [];
      if (visitsForSchedule.length === 0 && firstVisitDate) {
        visitsForSchedule = [
        {
          visitId: null,
          complaintId,
          visitDate: firstVisitDate,
          scheduledBy: managerId
        }];

      }

      const primaryVisitDate =
      firstVisitDate ||
      visitsForSchedule[0]?.visitDate ||
      '';

      // 1) Assign technician + set Scheduled (same update API as normal schedule save)
      const payload = buildUpdateComplaintPayload(ticket, {
        technicianId: selectedTechId,
        scheduledDate: primaryVisitDate,
        complaintStatus: 'Scheduled'
      });
      await updateCustomerComplaint(ticket.id, payload);

      // 2) Create visit rows via existing schedule API (same as handleSaveSchedule)
      const newVisits = visitsForSchedule.filter((visit) => {
        const id = Number(visit?.visitId);
        return !(Number.isFinite(id) && id > 0);
      });

      for (const visit of newVisits) {
        await scheduleComplaintVisit(ticket.id, visit.visitDate, managerId);
      }

      showToastMsg('Technician auto-assigned and visit scheduled successfully.');
      setIsReassigning(false);
      setAutoAssign(false);
      navigate('/fsm/customer-complaints', { replace: true });
    } catch (err) {
      console.error('Failed to auto-assign technician:', err);
      showToastMsg(
        err.status === 401 ?
        'Session expired. Please refresh and try again.' :
        err.message || 'Failed to auto-assign technician.',
        'error'
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCancelReassign = () => {
    const match = technicians.find((t) => String(t.id) === String(savedTechnicianId));
    setSelectedTechId(match?.id || String(savedTechnicianId));
    setIsReassigning(false);
  };

  // --- Save Changes: schedule visit(s) only (status Scheduled), then return to list ---
  const handleSaveSchedule = async () => {
    if (!ticket?.id || saveLoading) return;
    if (!firstVisitDate && scheduledVisits.length === 0) {
      showToastMsg('Please select the visit date and time.', 'error');
      return;
    }

    setSaveLoading(true);
    try {
      const managerId =
      ticket?.managerId ?? ticket?.raw?.managerId ?? sessionDB.getEmpId();
      const complaintId = ticket?.complaintId ?? ticket?.id;

      let visitsForSchedule = Array.isArray(scheduledVisits) ? [...scheduledVisits] : [];
      if (visitsForSchedule.length === 0 && firstVisitDate) {
        visitsForSchedule = [
        {
          visitId: null,
          complaintId,
          visitDate: firstVisitDate,
          scheduledBy: managerId
        }];

      }

      const primaryVisitDate =
      firstVisitDate ||
      visitsForSchedule[0]?.visitDate ||
      '';

      const payload = buildUpdateComplaintPayload(ticket, {
        technicianId: selectedTechId || ticket.technicianId || ticket.raw?.technicianId,
        scheduledDate: primaryVisitDate,
        complaintStatus: isScheduledOrLater ? ticket.statusRaw || ticket.status || 'Scheduled' : 'Scheduled'
      });
      await updateCustomerComplaint(ticket.id, payload);

      const newVisits = visitsForSchedule.filter((visit) => {
        const id = Number(visit?.visitId);
        return !(Number.isFinite(id) && id > 0);
      });

      for (const visit of newVisits) {
        await scheduleComplaintVisit(ticket.id, visit.visitDate, managerId);
      }

      showToastMsg('Visit scheduled successfully.');
      navigate('/fsm/customer-complaints', { replace: true });
    } catch (err) {
      console.error('Failed to schedule visit:', err);
      showToastMsg(
        err.status === 401 ?
        'Session expired. Please refresh and try again.' :
        err.message || 'Failed to schedule visit.',
        'error'
      );
    } finally {
      setSaveLoading(false);
    }
  };

  // --- Check priority mismatch ---
  
  const isSystemMismatch = useMemo(() => {
    if (!companyRecord) return false;
    return String(system || '').trim().toLowerCase() !==
    String(companyRecord.system || '').trim().toLowerCase();
  }, [system, companyRecord]);

  const isProductMismatch = useMemo(() => {
    if (!companyRecord) return false;
    return String(product || '').trim().toLowerCase() !==
    String(companyRecord.product || '').trim().toLowerCase();
  }, [product, companyRecord]);

  const isWarrantyMismatch = useMemo(() => {
    if (!companyRecord) return false;
    return String(warrantyPeriod || '').trim().toLowerCase() !==
    String(companyRecord.warranty || '').trim().toLowerCase();
  }, [warrantyPeriod, companyRecord]);

  const isAmcMismatch = useMemo(() => {
    if (!companyRecord) return false;
    return !amcTypesMatch(amc, companyRecord.amc);
  }, [amc, companyRecord]);

  // Banner reflects only the fields shown in Claim vs Records
  const hasMismatch = Boolean(
    companyRecord && (
    isSystemMismatch ||
    isProductMismatch ||
    isWarrantyMismatch ||
    isAmcMismatch)
  );

  const comparisonRows = useMemo(() => {
    return [
    {
      field: 'Services',
      valueA: system,
      valueB: records.system,
      isMismatch: isSystemMismatch
    },
    {
      field: 'Product',
      valueA: product || '—',
      valueB: records.product,
      isMismatch: isProductMismatch
    },
    {
      field: 'Warranty',
      valueA: warrantyPeriod || '—',
      valueB: records.warranty,
      isMismatch: isWarrantyMismatch
    },
    {
      field: 'AMC',
      valueA: amc || '—',
      valueB: records.amc,
      isMismatch: isAmcMismatch
    }];

  }, [system, product, warrantyPeriod, amc, records, isSystemMismatch, isProductMismatch, isWarrantyMismatch, isAmcMismatch]);

  // --- Style mappings ---
  const cardCls = 'mb-6';
  const labelCls = 'text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1';
  const valCls = 'text-[13.5px] font-semibold text-foreground';
  const inputCls = 'h-10 px-3 w-full border border-(--color-text-border) rounded-xl text-sm bg-white focus:outline-hidden focus:ring-2 focus:ring-accent/15 focus:border-accent transition disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed';

  if (loading) {
    return (
      <AppLayout formLayout="standard">
        <div className="mb-6">
          <PageTitleRow className="mb-5" back={<PageBackLink to="/fsm/customer-complaints" />}><h1><Trans i18nKey="fsm.complaintDetails" /></h1></PageTitleRow>
        </div>
        <LoadingState message="Loading complaint details..." />
      </AppLayout>);

  }

  if (!ticket || loadError) {
    return (
      <AppLayout formLayout="standard">
        <div className="mb-6">
          <PageTitleRow className="mb-5" back={<PageBackLink to="/fsm/customer-complaints" />}><h1><Trans i18nKey="fsm.complaintDetails" /></h1></PageTitleRow>
        </div>
        <EmptyState
          title={i18n.t("validation.unableToLoadComplaint")}
          message={loadError || 'Complaint not found.'} />
        
      </AppLayout>);

  }

  return (
    <AppLayout formLayout="standard">
      {/* Toast Alert */}
      {toast &&
      <div className={cn(
        "fixed bottom-6 right-6 z-50 p-4 rounded-xl shadow-lg flex items-center gap-3 animate-in fade-in slide-in-from-bottom-5 duration-300",
        toast.type === 'success' ? 'bg-emerald-500 text-white' : toast.type === 'warning' ? 'bg-amber-500 text-white' : 'bg-rose-500 text-white'
      )}>
          <span className="text-sm font-semibold">{toast.message}</span>
          <button onClick={() => setToast(null)} className="hover:opacity-85 text-white/90">
            <X className="h-4 w-4" />
          </button>
        </div>
      }

      {/* Header section with back navigation and status metadata */}
      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="flex flex-col gap-2.5">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <PageTitleRow back={<PageBackLink to="/fsm/customer-complaints" />}>
                <h1 className="text-[20px] font-bold text-foreground">
                <Trans i18nKey="fsm.complaintNumber" /> - {ticket.complaintNo || '—'}
              </h1>
              </PageTitleRow>
              {/* <span className={cn(
                 "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold",
                 TICKET_STATUS_CLASS[managerDisplayStatus] ||
                   "bg-primary/12 text-primary"
                )}>
                 <span className="h-1.5 w-1.5 rounded-full bg-current" />
                 {managerDisplayStatus}
                </span> */}
            </div>
            {/* <p className="text-xs font-semibold text-gray-500 mt-1">
               {ticket.customerName} &middot; {ticket.system} &middot; {complaintType}
              </p> */}
          </div>
        </div>

        <div className="shrink-0 flex items-center gap-2 flex-wrap">
          <span className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border border-current/20",
            TICKET_STATUS_CLASS[managerDisplayStatus] ||
            "text-primary",
            "bg-current/5"
          )}>
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {translateTicketStatus(managerDisplayStatus, i18n.t.bind(i18n))}
          </span>
          <span className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border border-current/20",
            getPriorityClass(ticket.priority),
            "bg-current/5"
          )}>
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {translateTicketPriority(ticket.priority, i18n.t.bind(i18n)) || "—"}
          </span>
        </div>
      </div>

      <div className="w-full">

        {/* SECTION 1: Complaint Details Overview */}
        <FsmCollapsibleCard
          className={cardCls}
          title={<Trans i18nKey="fsm.complaintDetails" />}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-5 form-grid">
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="common.customer2" /></span>
              <span className={valCls}>{ticket.customerName}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="common.location3" /></span>
              <span className={valCls}>{address}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="fsm.siteType" /></span>
              <span className={valCls}>{siteType}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="fsm.pointOfContact2" /></span>
              <span className={valCls}>{poc}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="common.contact2" /></span>
              <span className={valCls}>{contact}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="common.email2" /></span>
              <span className={valCls}>{email}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="fsm.complaintMode2" /></span>
              <span className={valCls}>{complaintMode}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="dateTime.complaintDate2" /></span>
              <span className={valCls}>{complaintDate}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="fsm.typeOfComplaint2" /></span>
              <span className={valCls}>{complaintType}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="fsm.callType" /></span>
              <span className={valCls}>{callType}</span>
            </div>
          </div>
          <div className="flex flex-col pt-3 border-t border-gray-100">
            <span className={labelCls}><Trans i18nKey="common.description" /></span>
            <p className="text-[13px] font-semibold text-gray-600 mt-1 leading-relaxed">{description}</p>
          </div>
        </FsmCollapsibleCard>

        {/* SECTION 1: Two Panel Claim vs Records Comparison */}
        <FsmCollapsibleCard
          className={cardCls}
          title={<Trans i18nKey="fsm.claimVsRecords" />}
          subtitle={<Trans i18nKey="fsm.whatTheCustomerReportedBesideWhatOurCompany" />}
          headerRight={
            companyRecordLoading ?
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-50 text-gray-500 border border-gray-200 text-xs font-bold">
                <Trans i18nKey="fsm.loadingRecords" />
              </span> :
            hasMismatch ?
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-xs font-bold">
                <AlertTriangle className="h-3.5 w-3.5" />
                <Trans i18nKey="fsm.mismatchedDetailsFound" />
              </span> :
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold">
                <Check className="h-3.5 w-3.5 font-bold" />
                <Trans i18nKey="fsm.allDetailsMatch" />
              </span>
          }>

          {/* DESKTOP COMPARISON VIEW */}
          <div className="hidden sm:block overflow-x-auto">
            <ClientTable className="w-full text-left text-xs min-w-[700px]">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="px-4 py-2.5 font-bold text-gray-400 uppercase tracking-wider w-[180px]"><Trans i18nKey="fsm.field" /></th>
                  <th className="px-4 py-2.5 font-bold text-blue-700 uppercase tracking-wider w-[260px]"><Trans i18nKey="fsm.customerSClaim" /></th>
                  <th className="px-4 py-2.5 font-bold text-blue-700 uppercase tracking-wider w-[260px]"><Trans i18nKey="fsm.companyRecords" /></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-[13px] font-semibold text-gray-700">
                
                {/* Customer Priority row */}
                {/* <tr className={cn(isPriorityMismatch && "bg-amber-50/50")}>
                   <td className="px-4 py-3 text-gray-500 font-medium">Customer priority</td>
                   <td className="px-4 py-3">{priority}</td>
                   <td className="px-4 py-3 flex items-center justify-between">
                     <span>{records.customerPriority}</span>
                     {isPriorityMismatch && (
                       <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800 font-bold border border-amber-200">
                         Mismatch
                       </span>
                     )}
                   </td>
                  </tr> */}

                {/* System row */}
                <tr className={cn(isSystemMismatch && "bg-amber-50/50")}>
                  <td className="px-4 py-3 text-gray-500 font-medium"><Trans i18nKey="common.services" /></td>
                  <td className="px-4 py-3">{system}</td>
                  <td className="px-4 py-3 flex items-center justify-between">
                    <span>{records.system}</span>
                    {isSystemMismatch ?
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800 font-bold border border-amber-200">
                        <Trans i18nKey="fsm.mismatch" />
                      </span> :

                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-800 font-bold border border-emerald-200">
                        <Trans i18nKey="fsm.match" />
                      </span>
                    }
                  </td>
                </tr>

                {/* Complaint Type row */}
                {/* <tr>
                   <td className="px-4 py-3 text-gray-500 font-medium">Type of complaint</td>
                   <td className="px-4 py-3">{complaintType}</td>
                   <td className="px-4 py-3">{records.complaintType}</td>
                  </tr> */}

                {/* Product row */}
                <tr className={cn(isProductMismatch && "bg-amber-50/50")}>
                  <td className="px-4 py-3 text-gray-500 font-medium"><Trans i18nKey="common.asset" /></td>
                  <td className="px-4 py-3">{product || '—'}</td>
                  <td className="px-4 py-3 flex items-center justify-between">
                    <span>{records.product}</span>
                    {isProductMismatch ?
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800 font-bold border border-amber-200">
                        <Trans i18nKey="fsm.mismatch" />
                      </span> :

                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-800 font-bold border border-emerald-200">
                        <Trans i18nKey="fsm.match" />
                      </span>
                    }
                  </td>
                </tr>

                {/* Warranty row */}
                <tr className={cn(isWarrantyMismatch && "bg-amber-50/50")}>
                  <td className="px-4 py-3 text-gray-500 font-medium"><Trans i18nKey="fsm.warranty" /></td>
                  <td className="px-4 py-3">{warrantyPeriod || '—'}</td>
                  <td className="px-4 py-3 flex items-center justify-between">
                    <span>{records.warranty}</span>
                    {isWarrantyMismatch ?
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800 font-bold border border-amber-200">
                        <Trans i18nKey="fsm.mismatch" />
                      </span> :

                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-800 font-bold border border-emerald-200">
                        <Trans i18nKey="fsm.match" />
                      </span>
                    }
                  </td>
                </tr>

                {/* AMC row */}
                <tr className={cn(isAmcMismatch && "bg-amber-50/50")}>
                  <td className="px-4 py-3 text-gray-500 font-medium"><Trans i18nKey="fsm.amc" /></td>
                  <td className="px-4 py-3">{amc || '—'}</td>
                  <td className="px-4 py-3 flex items-center justify-between">
                    <span>{records.amc}</span>
                    {isAmcMismatch ?
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800 font-bold border border-amber-200">
                        <Trans i18nKey="fsm.mismatch" />
                      </span> :

                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-800 font-bold border border-emerald-200">
                        <Trans i18nKey="fsm.match" />
                      </span>
                    }
                  </td>
                </tr>

              </tbody>
            </ClientTable>
          </div>

          {/* MOBILE COMPARISON VIEW */}
          <div className="block sm:hidden overflow-x-auto">
            <ClientTable className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="px-3 py-2.5 font-bold text-gray-400 uppercase tracking-wider"><Trans i18nKey="fsm.field" /></th>
                  <th className="px-3 py-2.5 font-bold text-gray-400 uppercase tracking-wider text-right"><Trans i18nKey="status.status2" /></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-[13px] font-semibold text-gray-700">
                {comparisonRows.map((row, idx) =>
                <tr
                  key={idx}
                  onClick={() => {
                    setSelectedComparisonRow(row);
                    setIsComparisonModalOpen(true);
                  }}
                  className={cn(
                    "cursor-pointer hover:bg-gray-50/80 active:bg-gray-100 transition-colors",
                    row.isMismatch && "bg-amber-50/20"
                  )}>
                  
                    <td className="px-3 py-3 text-gray-500 font-medium underline decoration-dotted decoration-primary">
                      {row.field}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {row.isMismatch ?
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800 font-bold border border-amber-200">
                          <Trans i18nKey="fsm.mismatch" />
                        </span> :

                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-800 font-bold border border-emerald-200">
                          <Trans i18nKey="fsm.match" />
                        </span>
                    }
                    </td>
                  </tr>
                )}
              </tbody>
            </ClientTable>
          </div>

          {/* Installation Info Badge in Claim vs Records card footer */}
          <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-500 font-semibold">
            <Clock className="h-4 w-4 text-gray-400" />
            <span>
              <span className="text-gray-700">{records.installationCode}</span>
              {' '}
              <Trans i18nKey="fsm.installed" /> {records.installationDate}
              {records.installationNote ? `. ${records.installationNote}` : ''}
            </span>
          </div>
        </FsmCollapsibleCard>

        {/* SECTION 2: Manager review actions (pending_review) */}
        {showTechRejectionBanner &&
        <div className="rounded-2xl border border-amber-200 bg-amber-50/80 px-4 sm:px-5 py-4 mb-6">
            <p className="text-[11px] font-bold text-amber-800 uppercase tracking-wider mb-2">
              <Trans i18nKey="fsm.technicianJobRejection" />
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm form-grid">
              <div>
                <p className="text-[11px] font-bold text-amber-700/80 uppercase tracking-wider mb-0.5">
                  <Trans i18nKey="formActions.rejectedBy" />
                </p>
                <p className="font-semibold text-foreground">
                  {rejectedByName || '—'}
                </p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-[11px] font-bold text-amber-700/80 uppercase tracking-wider mb-0.5">
                  <Trans i18nKey="formActions.rejectionReason" />
                </p>
                <p className="font-semibold text-foreground leading-relaxed whitespace-pre-wrap">
                  {techRejectionReason}
                </p>
              </div>
            </div>
          </div>
        }

        {showManagerReviewActions &&
        <div className="bg-white rounded-2xl border border-(--color-text-border)/60 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.22)] mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 form-surface form-section">
            <p className="text-sm per-file-hint leading-relaxed max-w-2xl">
              <Trans i18nKey="formActions.verifyTheComparisonThenDecide" />{' '}
              <span className="font-bold text-foreground"><Trans i18nKey="fsm.proceed" /></span> <Trans i18nKey="fsm.toAssignment" />{' '}
              <span className="font-bold text-foreground"><Trans i18nKey="fsm.hold" /></span> <Trans i18nKey="fsm.forClarificationOr" />{' '}
              <span className="font-bold text-foreground"><Trans i18nKey="formActions.reject" /></span> <Trans i18nKey="fsm.ifServiceDoesNotApply" />
            </p>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0 self-end sm:self-auto">
              <button
              type="button"
              disabled={Boolean(statusActionLoading)}
              onClick={handleRejectConfirm}
              className="h-10 px-4 sm:px-5 rounded-xl border border-error text-error bg-white font-semibold text-sm hover:bg-error/5 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
              
                {statusActionLoading === MANAGER_STATUS_ACTIONS.reject ? i18n.t('fsm.rejecting') : <Trans i18nKey="formActions.reject" />}
              </button>
              <button
              type="button"
              disabled={Boolean(statusActionLoading)}
              onClick={handleHoldConfirm}
              className="h-10 px-4 sm:px-5 rounded-xl border border-warning text-warning bg-white font-semibold text-sm hover:bg-warning/5 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
              
                {statusActionLoading === MANAGER_STATUS_ACTIONS.hold ? i18n.t('fsm.holding') : <Trans i18nKey="fsm.hold" />}
              </button>
              <button
              type="button"
              disabled={Boolean(statusActionLoading)}
              onClick={handleProceed}
              className={cn(ui.primaryBtn, 'h-10 px-4 sm:px-5 shadow-none')}>
              
                {statusActionLoading === MANAGER_STATUS_ACTIONS.proceed ? i18n.t('fsm.proceeding') : <Trans i18nKey="fsm.proceed" />}
              </button>
            </div>
          </div>
        }

        {showOnHoldActions &&
        <div className="bg-white rounded-2xl border border-warning/40 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.22)] mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 form-surface form-section">
            <p className="text-sm per-file-hint leading-relaxed max-w-2xl">
              <Trans i18nKey="fsm.thisTicketIsOnHold" />{' '}
              <span className="font-bold text-foreground"><Trans i18nKey="fsm.proceed" /></span> <Trans i18nKey="fsm.toContinueAssignmentOr" />{' '}
              <span className="font-bold text-foreground"><Trans i18nKey="formActions.reject" /></span> <Trans i18nKey="fsm.ifServiceDoesNotApply" />
            </p>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0 self-end sm:self-auto">
              <button
              type="button"
              disabled={Boolean(statusActionLoading)}
              onClick={handleRejectConfirm}
              className="h-10 px-4 sm:px-5 rounded-xl border border-error text-error bg-white font-semibold text-sm hover:bg-error/5 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
              
                {statusActionLoading === MANAGER_STATUS_ACTIONS.reject ? i18n.t('fsm.rejecting') : <Trans i18nKey="formActions.reject" />}
              </button>
              <button
              type="button"
              disabled={Boolean(statusActionLoading)}
              onClick={handleProceed}
              className={cn(ui.primaryBtn, 'h-10 px-4 sm:px-5 shadow-none')}>
                <Trans i18nKey="fsm.proceed" />
              
            </button>
            </div>
          </div>
        }



        {/* Technician Details — shown after Proceed, or for non-pending tickets */}
        {showTechnicianCard &&
        <FsmCollapsibleCard
          className={cardCls}
          title={<Trans i18nKey="fsm.technicianDetails" />}
          headerRight={
            <>
              {hasSavedTechnician && !isReassigning && (isAcceptedStatus || isScheduledOrLater) ?
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 text-[11px] font-semibold border border-sky-100">
                  <Trans i18nKey="fsm.lockedTechnicianAccepted" />
                </span> :
              null}
              {canReassignTechnician ?
              <div className="hidden sm:block">
                {isReassigning ?
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelReassign}
                  className="h-9 px-3 rounded-xl border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 bg-white">
                  <Trans i18nKey="formActions.cancel" />
                </Button> :
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsReassigning(true)}
                  className="h-9 px-3 rounded-xl border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 bg-white">
                  <Trans i18nKey="fsm.reassignTechnician" />
                </Button>}
              </div> :
              null}
            </>
          }>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-4 form-grid">
            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-500 mb-1"><Trans i18nKey="fsm.technicianName" /></label>
              <Select
                value={selectedTechId}
                onValueChange={(val) => setSelectedTechId(val)}
                disabled={techniciansLoading || saveLoading || !assignmentEditable}>
                
                <SelectTrigger className="h-10 rounded-xl border-(--color-text-border) bg-white text-sm focus:ring-accent/15 focus:border-accent disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed">
                  <SelectValue
                    placeholder={
                    techniciansLoading ?
                    'Loading technicians...' :
                    techniciansError || 'Assign a technician'
                    } />
                  
                </SelectTrigger>
                <SelectContent>
                  {technicians.map((t) =>
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {techniciansError && !techniciansLoading ?
              <p className="text-xs text-error font-medium mt-1">{techniciansError}</p> :
              null}
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-500 mb-1"><Trans i18nKey="common.contact3" /></label>
              <input
                type="text"
                readOnly
                value={techContactReadOnly}
                placeholder="—"
                className="h-10 px-3 w-full border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-500 outline-hidden" />
              
            </div>
          </div>

          {assignmentEditable ?
          <label className="inline-flex items-center gap-2.5 cursor-pointer select-none mb-4">
            <input
              type="checkbox"
              checked={autoAssign}
              disabled={saveLoading}
              onChange={(e) => {
                const checked = e.target.checked;
                setAutoAssign(checked);
                if (checked) {
                  // Start blank so the manager must choose visit date/time
                  // (do not inherit complaint received / current datetime).
                  setFirstVisitDate('');
                  setScheduledVisits((prev) =>
                    (Array.isArray(prev) ? prev : []).filter((visit) => {
                      const id = Number(visit?.visitId);
                      return Number.isFinite(id) && id > 0;
                    })
                  );
                }
              }}
              className="h-4 w-4 rounded border-(--color-text-border) text-primary accent-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" />
            
            <span className="text-sm font-semibold text-foreground">
              <Trans i18nKey="fsm.autoAssign" />
            </span>
          </label> :
          null}

          {canReassignTechnician ?
          <div className="sm:hidden">
            {isReassigning ?
            <Button
              type="button"
              variant="outline"
              onClick={handleCancelReassign}
              className="h-9 px-3 rounded-xl border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 bg-white">
              <Trans i18nKey="formActions.cancel" />
            </Button> :
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsReassigning(true)}
              className="h-9 px-3 rounded-xl border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 bg-white">
              <Trans i18nKey="fsm.reassignTechnician" />
            </Button>}
          </div> :
          null}

        </FsmCollapsibleCard>
        }

        {/* SECTION 7: Scheduling — after technician accepts, or during Auto Assign */}
        {showSchedulingSection &&
        <FsmCollapsibleCard
          className={cardCls}
          title={<Trans i18nKey="fsm.scheduling" />}>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex flex-col min-w-[220px] flex-1 max-w-sm">
                <label className="text-xs font-semibold text-gray-500 mb-1"><Trans i18nKey="dateTime.firstVisitDate" /></label>
                <input
                  type="datetime-local"
                  step="60"
                  value={firstVisitDate}
                  onChange={(e) => handleFirstVisitChange(e.target.value)}
                  disabled={saveLoading || !canEditFirstVisit}
                  className={inputCls} />
                
              </div>
              <button
                type="button"
                onClick={openAnotherVisitPopup}
                disabled={saveLoading || isClosureRequested || isClosureVerified || isClosedStatus}
                className="h-10 px-3.5 rounded-xl border border-(--color-text-border) bg-white text-xs font-bold text-primary hover:bg-workspace transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5 shrink-0">
                
                <Plus className="h-3.5 w-3.5" />
                <Trans i18nKey="formActions.addConsecutiveVisit" />
              </button>
            </div>

            {scheduledVisits.length > 0 ?
            <div className="flex flex-col gap-2.5 pt-1">
                {[...scheduledVisits]
              .sort((a, b) => {
                const parseAt = (v) =>
                Date.parse(String(v?.visitDate || v?.createdOn || '').replace(' ', 'T')) || 0;
                return parseAt(a) - parseAt(b);
              })
              .map((visit, idx) =>
              <div
                key={visit.visitId ?? `${visit.visitDate}-${idx}`}
                className="flex items-center gap-3 text-sm">
                
                      <span className="h-6 w-6 rounded-full bg-sky-100 text-sky-800 text-[11px] font-bold inline-flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                      {/* <span className="font-bold text-foreground"><Trans i18nKey="fsm.visit" /> {idx + 1}</span> */}
                      <span className="text-gray-500 font-medium">
                        {formatVisitDateTime(visit.visitDate)}
                      </span>
                    </div>
              )}
              </div> :
            firstVisitDate ?
            <div className="flex items-center gap-3 text-sm pt-1">
                <span className="h-6 w-6 rounded-full bg-sky-100 text-sky-800 text-[11px] font-bold inline-flex items-center justify-center shrink-0">
                  1
                </span>
                <span className="text-gray-500 font-medium">
                  {formatVisitDateTime(firstVisitDate)}
                </span>
              </div> :
            null}
          </div>
        </FsmCollapsibleCard>
        }

        {/* SECTION 5: Checklists — readonly statuses from complaint checklistItems */}
        {showAssignmentDetails &&
        <FsmCollapsibleCard
          className={cardCls}
          title={<Trans i18nKey="fsm.checklists" />}
          subtitle={<Trans i18nKey="fsm.itemsMatchedToThisTicketSProductService" />}>

          {checklistsLoading ?
          <p className="text-xs per-file-hint font-medium"><Trans i18nKey="fsm.loadingChecklists" /></p> :
          checklistsError ?
          <p className="text-xs text-error font-medium">{checklistsError}</p> :

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 form-grid">
              <div className="border border-gray-100 rounded-xl p-4 flex flex-col bg-gray-50/20 gap-3">
                <h3 className="text-xs font-bold text-foreground"><Trans i18nKey="fsm.internalAssetChecklist" /></h3>
                {internalChecklistItems.length === 0 ?
              <p className="text-xs text-gray-400 italic"><Trans i18nKey="fsm.noInternalAssetItemsForThisComplaintType" /></p> :

              internalChecklistItems.map((item, idx) => {
                const prior = findLatestChecklistSubmission(
                  checklistSubmissions,
                  item.checklistItemId ?? item.id,
                  'Internal Asset'
                );
                const status = normalizeChecklistStatus(
                  getChecklistSubmissionStatus(prior)
                );
                return (
                  <div
                    key={item.id || idx}
                    className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                    
                        <p className="text-xs font-semibold text-gray-700 min-w-0 flex-1">
                          <span className="text-gray-400 mr-1.5">{idx + 1}.</span>
                          {item.whatToCheck}
                        </p>
                        <span
                      className={cn(
                        'shrink-0 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase self-start sm:self-auto',
                        status === 'Completed' || status === 'Not Applicable' ?
                        'bg-emerald-100 text-emerald-800' :
                        status === 'In Progress' ?
                        'bg-sky-100 text-sky-800' :
                        'bg-gray-100 text-gray-600'
                      )}>
                      
                          {status}
                        </span>
                      </div>);

              })
              }
              </div>

              <div className="border border-gray-100 rounded-xl p-4 flex flex-col bg-gray-50/20 gap-3">
                <h3 className="text-xs font-bold text-foreground"><Trans i18nKey="fsm.fieldServiceChecklist" /></h3>
                {fieldChecklistItems.length === 0 ?
              <p className="text-xs text-gray-400 italic"><Trans i18nKey="fsm.noFieldServiceItemsForThisComplaintType" /></p> :

              fieldChecklistItems.map((item, idx) => {
                const prior = findLatestChecklistSubmission(
                  checklistSubmissions,
                  item.checklistItemId ?? item.id,
                  'Field Service'
                );
                const status = normalizeChecklistStatus(
                  getChecklistSubmissionStatus(prior)
                );
                return (
                  <div
                    key={item.id || idx}
                    className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                    
                        <p className="text-xs font-semibold text-gray-700 min-w-0 flex-1">
                          <span className="text-gray-400 mr-1.5">{idx + 1}.</span>
                          {item.whatToCheck}
                        </p>
                        <span
                      className={cn(
                        'shrink-0 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase self-start sm:self-auto',
                        status === 'Completed' || status === 'Not Applicable' ?
                        'bg-emerald-100 text-emerald-800' :
                        status === 'In Progress' ?
                        'bg-sky-100 text-sky-800' :
                        'bg-gray-100 text-gray-600'
                      )}>
                      
                          {status}
                        </span>
                      </div>);

              })
              }
              </div>
            </div>
          }
        </FsmCollapsibleCard>
        }

        {showQuotationSection &&
        <FsmCollapsibleCard
          className={cardCls}
          title={<Trans i18nKey="fsm.quotation" />}
          headerRight={
            hasQuotations ?
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-workspace text-primary border border-(--color-text-border)/50">
                {quotations.length}
              </span> :
            null
          }>
          {quotationsLoading ?
          <p className="text-xs per-file-hint font-medium mb-4">Loading quotations…</p> :
          quotationsError && !hasQuotations ?
          <p className="text-xs text-error font-medium mb-4">{quotationsError}</p> :
          !hasQuotations ?
          <p className="text-xs text-gray-400 italic mb-4"><Trans i18nKey="fsm.noQuotationRaisedYet" /></p> :
          <div className="space-y-2.5 mb-4">
              {quotations.map((quotation) =>
            <FsmQuotationCard
              key={quotation.id || quotation.quotationNumber}
              quotation={quotation}
              returnTo={`/fsm/customer-complaints/${ticket?.id ?? id}`} />
            )}
            </div>}
          {canRaiseQuotation && complaintIdForQuotations != null && complaintIdForQuotations !== '' ?
          <div className="flex justify-end">
              <button
              type="button"
              onClick={handleRaiseQuotation}
              className="h-10 px-6 bg-primary hover:bg-accent text-white font-semibold text-sm rounded-xl transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer w-full sm:w-auto">
                <Trans i18nKey="fsm.raiseQuotation" />
              </button>
            </div> :
          null}
        </FsmCollapsibleCard>
        }

        {isRejected &&
        <div className="rounded-2xl border border-error/30 bg-error/5 px-4 sm:px-5 py-3.5 mb-6">
            <p className="text-sm font-semibold text-error">
              <Trans i18nKey="fsm.thisTicketHasBeenRejectedNoFurtherAssignment" />
            </p>
          </div>
        }

        {showClosurePanel &&
        <FsmCollapsibleCard
          className={cardCls}
          title={<Trans i18nKey="fsm.closureVerification" />}>

          <div className="space-y-4">
            <ReadOnlyField label={i18n.t('fsm.resolutionReasonForClosure')}>
              {resolutionText || '—'}
            </ReadOnlyField>

            {isClosureRequested && !canVerifyClosure ?
            <div className="rounded-xl border border-warning/30 bg-warning/5 px-3 py-2.5">
                <p className="text-sm font-semibold text-warning">
                  <Trans i18nKey="fsm.waitingForCustomerServiceVerification" />
                </p>
              </div> :
            null}

            {canVerifyClosure ?
            <div className="space-y-4 pt-1">
                <div className="pb-2 border-b border-gray-100">
                  <h3 className="text-[13px] font-bold text-foreground">
                    <Trans i18nKey="fsm.customerVerification" />
                  </h3>
                </div>

                <div className="flex flex-col gap-1.5 max-w-sm">
                  <label className="text-xs font-semibold text-gray-500">
                    <Trans i18nKey="fsm.closureMode" /> <span className="text-error">*</span>
                  </label>
                  <Select
                  value={closureMode || undefined}
                  onValueChange={setClosureMode}
                  disabled={Boolean(statusActionLoading)}>
                  
                    <SelectTrigger className="h-10 rounded-xl border-(--color-text-border) bg-white text-sm">
                      <SelectValue placeholder={i18n.t('fsm.selectClosureMode')} />
                    </SelectTrigger>
                    <SelectContent>
                      {CLOSURE_MODE_OPTIONS.map((opt) =>
                    <SelectItem key={opt.value} value={opt.value}>
                          <Trans i18nKey={opt.labelKey} />
                        </SelectItem>
                    )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-500">
                    <Trans i18nKey="fsm.customerServiceRemarks" /> <span className="text-error">*</span>
                  </label>
                  <Textarea
                  value={customerServiceRemarks}
                  onChange={(e) => setCustomerServiceRemarks(e.target.value)}
                  disabled={Boolean(statusActionLoading)}
                  rows={3}
                  className="rounded-xl border-(--color-text-border) text-sm" />
                
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-500">
                    <Trans i18nKey="fsm.customerRatings" /> <span className="text-error">*</span>
                  </label>
                  <RatingPicker
                  value={customerRatings}
                  onChange={setCustomerRatings}
                  disabled={Boolean(statusActionLoading)} />
                
                </div>
              </div> :
            null}

            {(isClosureVerified || isClosedStatus) &&
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-xl border border-(--color-text-border)/40 px-3 py-3 bg-workspace/20 form-grid">
                <ReadOnlyField label={i18n.t('fsm.closureMode')}>
                  {closureModeLabel}
                </ReadOnlyField>
                <ReadOnlyField label={i18n.t('fsm.customerRatings')}>
                  {customerRatings != null ? `${customerRatings} / 5` : '—'}
                </ReadOnlyField>
                <div className="sm:col-span-2">
                  <ReadOnlyField label={i18n.t('fsm.customerServiceRemarks')}>
                    {String(customerServiceRemarks || '').trim() || '—'}
                  </ReadOnlyField>
                </div>
              </div>
            }

            {isClosureVerified && !canManagerClose ?
            <div className="rounded-xl border border-accent/30 bg-accent/5 px-3 py-2.5">
                <p className="text-sm font-semibold text-primary">
                  <Trans i18nKey="fsm.closureVerifiedWaitingForManagerToClose" />
                </p>
              </div> :
            null}

            {canManagerClose ?
            <div className="space-y-4 pt-1">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-500">
                    <Trans i18nKey="common.managerRemarks" /> <span className="text-error">*</span>
                  </label>
                  <Textarea
                  value={managerRemarks}
                  onChange={(e) => setManagerRemarks(e.target.value)}
                  disabled={Boolean(statusActionLoading)}
                  rows={3}
                  className="rounded-xl border-(--color-text-border) text-sm" />
                
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-500">
                    <Trans i18nKey="fsm.managerRatings" /> <span className="text-error">*</span>
                  </label>
                  <RatingPicker
                  value={managerRatings}
                  onChange={setManagerRatings}
                  disabled={Boolean(statusActionLoading)} />
                
                </div>
              </div> :
            null}

            {isClosedStatus ?
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-xl border border-(--color-text-border)/40 px-3 py-3 form-grid">
                <ReadOnlyField label={i18n.t('common.managerRemarks')}>
                  {String(managerRemarks || '').trim() || '—'}
                </ReadOnlyField>
                <ReadOnlyField label={i18n.t('fsm.managerRatings')}>
                  {managerRatings != null ? `${managerRatings} / 5` : '—'}
                </ReadOnlyField>
              </div> :
            null}
          </div>
        </FsmCollapsibleCard>
        }

        {/* BOTTOM ACTION BAR — Save Changes, Verify Closure, or Close Ticket */}
        {!isRejected && !isClosedStatus &&
        <div className="flex items-center gap-3 justify-end mb-8">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/fsm/customer-complaints')}
            className="h-10 px-6 rounded-xl border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 bg-white">
            <Trans i18nKey="formActions.cancel" />
          
          </Button>
          {canVerifyClosure ?
          <button
            type="button"
            onClick={handleVerifyClosure}
            disabled={Boolean(statusActionLoading)}
            className="h-10 px-6 bg-primary hover:bg-accent text-white font-semibold text-sm rounded-xl transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
            
              <Check className="h-4 w-4" />
              {statusActionLoading === 'Closure_Verified' ?
            i18n.t('fsm.savingEllipsis') :
            <Trans i18nKey="fsm.verifyClosure" />
            }
            </button> :
          canManagerClose ?
          <button
            type="button"
            onClick={handleCloseTicket}
            disabled={Boolean(statusActionLoading)}
            className="h-10 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm rounded-xl transition shadow-[0_8px_20px_-8px_rgba(16,185,129,0.45)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
            
              <Check className="h-4 w-4" />
              {statusActionLoading === 'Closed' ? i18n.t('fsm.closing') : <Trans i18nKey="fsm.closeTicket" />}
            </button> :
          canSaveAssignment ?
          <button
            type="button"
            onClick={autoAssign ? handleSaveAutoAssign : handleSaveAssignment}
            disabled={saveLoading}
            className="h-10 px-6 bg-primary hover:bg-accent text-white font-semibold text-sm rounded-xl transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
            
              {saveLoading ? i18n.t('fsm.savingEllipsis') : <Trans i18nKey="fsm.saveChanges" />}
            </button> :
          canSaveSchedule ?
          <button
            type="button"
            onClick={handleSaveSchedule}
            disabled={saveLoading}
            className="h-10 px-6 bg-primary hover:bg-accent text-white font-semibold text-sm rounded-xl transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
            
              {saveLoading ? i18n.t('fsm.savingEllipsis') : <Trans i18nKey="fsm.saveChanges" />}
            </button> :
          null}
        </div>
        }

        {(isRejected || isClosedStatus) &&
        <div className="flex items-center justify-end mb-8">
            <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/fsm/customer-complaints')}
            className="h-10 px-6 rounded-xl border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 bg-white">
              <Trans i18nKey="formActions.backToTickets" />
            
          </Button>
          </div>
        }

      </div>

      {/* --- CONFIRMATION MODAL: HOLD --- */}
      {isHoldOpen &&
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl max-w-md w-full p-5 flex flex-col gap-4 animate-in slide-in-from-bottom-4 duration-300">
            <div>
              <h3 className="text-base font-bold text-foreground flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-500" />
                <Trans i18nKey="fsm.putTicketOnHold" />
              </h3>
              <p className="text-xs text-gray-500 font-medium mt-1"><Trans i18nKey="fsm.specifyWhyTheTicketIsBeingHeldFrom" /></p>
            </div>
            <textarea
            placeholder={i18n.t("examples.eGAwaitingClientConfirmationOfDowntimeWindow")}
            value={holdReason}
            onChange={(e) => setHoldReason(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-hidden focus:ring-2 focus:ring-amber-500/15 focus:border-amber-500 transition resize-none" />
          
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
              type="button"
              onClick={() => setIsHoldOpen(false)}
              className="h-9 px-4 rounded-xl border border-gray-200 bg-white text-xs font-bold text-gray-700 hover:bg-gray-50 cursor-pointer">
                <Trans i18nKey="formActions.cancel" />
              
            </button>
              <button
              type="button"
              onClick={handleHoldConfirm}
              className="h-9 px-4 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-xl cursor-pointer">
                <Trans i18nKey="fsm.confirmHold" />
              
            </button>
            </div>
          </div>
        </div>
      }

      {/* --- CONFIRMATION MODAL: REJECT --- */}
      {isRejectOpen &&
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl max-w-md w-full p-5 flex flex-col gap-4 animate-in slide-in-from-bottom-4 duration-300">
            <div>
              <h3 className="text-base font-bold text-foreground flex items-center gap-2">
                <X className="h-5 w-5 text-rose-600" />
                <Trans i18nKey="formActions.rejectTicketClaim" />
              </h3>
              <p className="text-xs text-gray-500 font-medium mt-1"><Trans i18nKey="validation.specifyTheRejectionReasonRequired" /></p>
            </div>
            <textarea
            placeholder={i18n.t("examples.eGAssetNotInstalledAtClientFacility")}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-hidden focus:ring-2 focus:ring-rose-500/15 focus:border-rose-500 transition resize-none" />
          
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
              type="button"
              onClick={() => setIsRejectOpen(false)}
              className="h-9 px-4 rounded-xl border border-gray-200 bg-white text-xs font-bold text-gray-700 hover:bg-gray-50 cursor-pointer">
                <Trans i18nKey="formActions.cancel" />
              
            </button>
              <button
              type="button"
              onClick={handleRejectConfirm}
              className="h-9 px-4 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!rejectReason.trim()}>
                <Trans i18nKey="fsm.confirmReject" />
              
            </button>
            </div>
          </div>
        </div>
      }

      {/* --- SCHEDULE ANOTHER VISIT MODAL --- */}
      {isAnotherVisitOpen &&
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl max-w-md w-full p-5 flex flex-col gap-4 animate-in slide-in-from-bottom-4 duration-300">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-foreground flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  <Trans i18nKey="fsm.scheduleConsecutiveVisit" />
                </h3>
                <p className="text-xs text-gray-500 font-medium mt-1">
                  <Trans i18nKey="dateTime.chooseTheDateAndTimeForTheAdditional" />
                </p>
              </div>
              <button
              type="button"
              onClick={() => {
                setIsAnotherVisitOpen(false);
                setAnotherVisitDate('');
              }}
              className="text-gray-400 hover:text-gray-600">
              
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-500 mb-1">
                <Trans i18nKey="dateTime.visitDateTime" /> <span className="text-error">*</span>
              </label>
              <input
              type="datetime-local"
              step="60"
              value={anotherVisitDate}
              onChange={(e) => setAnotherVisitDate(e.target.value)}
              className={inputCls} />
            
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
              type="button"
              onClick={() => {
                setIsAnotherVisitOpen(false);
                setAnotherVisitDate('');
              }}
              className="h-9 px-4 rounded-xl border border-gray-200 bg-white text-xs font-bold text-gray-700 hover:bg-gray-50 cursor-pointer">
                <Trans i18nKey="formActions.cancel" />
              
            </button>
              <button
              type="button"
              onClick={handleAddConsecutiveVisit}
              disabled={!anotherVisitDate}
              className={cn(ui.primaryBtn, 'h-9 px-4 text-xs disabled:opacity-50')}>
                <Trans i18nKey="common.add" />
              
            </button>
            </div>
          </div>
        </div>
      }

      {/* --- COMPARISON DETAILS MODAL (MOBILE) --- */}
      {isComparisonModalOpen && selectedComparisonRow &&
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl max-w-md w-full p-5 flex flex-col gap-4 animate-in slide-in-from-bottom-4 duration-300">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="text-base font-bold text-foreground">
                <Trans i18nKey="common.details" /> {selectedComparisonRow.field}
              </h3>
              <button
              onClick={() => {
                setIsComparisonModalOpen(false);
                setSelectedComparisonRow(null);
              }}
              className="text-gray-400 hover:text-gray-600">
              
                <X className="h-4 w-4" />
              </button>
            </div>
            
            <div className="flex flex-col gap-4">
              <div className="bg-blue-50/40 border border-blue-200/50 rounded-xl p-3.5">
                <p className="text-[10px] font-bold text-blue-800 uppercase tracking-wider">
                  <Trans i18nKey="fsm.customerSClaim2" />
                </p>
                <p className="text-[14px] font-bold text-foreground mt-1.5">
                  {selectedComparisonRow.valueA}
                </p>
              </div>

              <div className="bg-blue-50/40 border border-blue-200/50 rounded-xl p-3.5">
                <p className="text-[10px] font-bold text-blue-800 uppercase tracking-wider">
                  <Trans i18nKey="fsm.companyRecords2" />
                </p>
                <p className="text-[14px] font-bold text-foreground mt-1.5">
                  {selectedComparisonRow.valueB}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end pt-2">
              <button
              type="button"
              onClick={() => {
                setIsComparisonModalOpen(false);
                setSelectedComparisonRow(null);
              }}
              className="h-9 px-6 bg-primary hover:bg-accent text-white text-xs font-bold rounded-xl cursor-pointer">
                <Trans i18nKey="formActions.close" />
              
            </button>
            </div>
          </div>
        </div>
      }
    </AppLayout>);

}
